// What the keyboard offers now (docs/design/ux-review.md, unit 6): at most four hints for the
// current state in the status bar, the two or three actions the active Segment's state calls for,
// and the full map of chords grouped by menu. The completion popup, the entry popover and the
// split picker own the hints while open. Pure, so node --test checks them.

import type { ReviewKind } from '../engine/protocol.ts'
import type { Command } from './registry.ts'
import { menuLabel, menusOf, placedAt, type MenuHost, type MenuTable, type ShellMenus } from './menu.ts'

export interface HintContext {
  /// The project's target language by name.
  target: string
  completion: boolean
  /// The term, flag or link popover is open.
  entry: boolean
  splitting: boolean
  /// The split picker offers to keep the target on the first piece.
  splitKeeps: boolean
  finding: boolean
  review: ReviewKind | null
  editable: boolean
  /// The active editor holds text and lacks a Protected Object.
  missing: boolean
  unlock: boolean
}

/// One hint: the commands whose chords it shows, or the popup's own keys, and what they do.
export type Hint = { commands: string[]; keys?: undefined; label: string } | { keys: string[]; commands?: undefined; label: string }

const c = (label: string, ...commands: string[]): Hint => ({ commands, label })
const k = (label: string, ...keys: string[]): Hint => ({ keys, label })

export function hintsFor(s: HintContext): Hint[] {
  if (s.completion) return [k('choose', 'Up', 'Down'), k('insert', 'Enter', 'Tab'), k('dismiss', 'Esc')]
  if (s.entry) return [k('save', 'Enter'), k('cancel', 'Esc')]
  if (s.splitting) return [c('move a word', 'split.previous', 'split.next'), c('split here', 'split.cut'), ...(s.splitKeeps ? [c(`keep the ${s.target} on the first piece`, 'split.cutKeep')] : []), c('cancel', 'escape')]
  if (s.finding) return [c('next hit', 'find.next'), c('previous hit', 'find.previous'), c('close the find', 'escape'), c('commands', 'palette.open')]
  if (s.review === null) return [c('next untranslated', 'segment.nextUntranslated'), c('commands', 'palette.open')]
  if (!s.editable) return [c('next', 'segment.next'), c('next untranslated', 'segment.nextUntranslated'), ...(s.unlock ? [c('unlock', 'segment.unlock')] : []), c('commands', 'palette.open')]
  if (s.missing) return [c('confirm', 'segment.confirm'), c('place the missing object', 'editor.placeMissing'), c('next untranslated', 'segment.nextUntranslated'), c('commands', 'palette.open')]
  return [c('confirm', 'segment.confirm'), c('next untranslated', 'segment.nextUntranslated'), c('suggestions', 'editor.complete'), c('commands', 'palette.open')]
}

export interface ActionContext {
  review: ReviewKind
  readOnly: boolean
  /// Memory matches for the Segment.
  matches: number
  mayApprove: boolean
  mayUnlock: boolean
}

/// The active Segment's actions for its state, on the disclosure model's meta line: each a control a
/// pointer presses, the same commands the hints name.
export function rowActions(s: ActionContext): { command: string; label: string }[] {
  if (s.readOnly) return []
  switch (s.review) {
    case 'untouched':
      return [...(s.matches > 0 ? [{ command: 'match.insert1', label: 'Insert match 1' }] : []), { command: 'segment.keepSource', label: 'Keep source' }, { command: 'segment.markLeftEmpty', label: 'Left empty' }]
    case 'unreviewed':
      return [{ command: 'segment.confirm', label: 'Confirm' }]
    case 'confirmed':
      return s.mayApprove ? [{ command: 'segment.approve', label: 'Approve' }] : []
    case 'approved':
      return s.mayUnlock ? [{ command: 'segment.unlock', label: 'Unlock' }] : []
  }
}

/// What the full map calls the keys no menu lists: Escape, the split picker's, a pane item's.
export const UNPLACED_TITLE = 'Without a menu'

/// Every command with a chord, as [title, chord, help], under the menu that lists it in menu order, as
/// the Mac's Keyboard settings list an app's shortcuts, then the keys no menu lists; a menu with
/// none is left out.
export function shortcutMap<S, T extends MenuTable = ShellMenus>(commands: readonly Pick<Command<S, T>, 'id' | 'title' | 'menu' | 'help'>[], chord: (id: string) => string | null, host: MenuHost<T>): { title: string; rows: [string, string, string | null][] }[] {
  const rows = (keep: (c: Pick<Command<S, T>, 'menu'>) => boolean) => commands.filter(keep).flatMap((cmd): [string, string, string | null][] => {
    const shown = chord(cmd.id)
    return shown ? [[cmd.title, shown, cmd.help ?? null]] : []
  })
  return [
    ...menusOf(host).map(id => ({ title: menuLabel(host, id), rows: rows(c => c.menu.kind === 'item' && placedAt(c.menu, host).menu === id) })),
    { title: UNPLACED_TITLE, rows: rows(c => c.menu.kind === 'unplaced') },
  ].filter(task => task.rows.length > 0)
}
